import React, { useMemo } from "react";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { Col, Container, Row, Card } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiClipboardEditOutline,
  mdiCheckDecagramOutline,
  mdiChartTimelineVariant,
  mdiMonitorDashboard,
} from "@mdi/js";
import image from "../assets/img/photos/login-wallpaper.jpg";
import LoadingPage from "../components/common/LoadingPage";

// The four core SMEPPS modules, shown on the welcome side.
const MODULES = [
  { label: "Permohonan", icon: mdiClipboardEditOutline },
  { label: "Perakuan", icon: mdiCheckDecagramOutline },
  { label: "Pelaporan", icon: mdiChartTimelineVariant },
  { label: "Pemantauan", icon: mdiMonitorDashboard },
];

const pageStyles = `
  .signin-resource { background: #fff; transition: border-color .15s, background-color .15s, transform .15s; }
  .signin-resource:hover, .signin-resource:focus-visible { border-color: var(--bs-primary, #3b7ddd) !important; background: #f5f9ff; transform: translateY(-1px); }
  .signin-module { background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.25); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
`;

const SignInLayout = ({ children }) => {
  const isLoading = useSelector((state) => state.userReducer?.isLoading);

  // Smart Malay greeting based on local time
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return "Selamat Pagi";
    if (h >= 12 && h < 18) return "Selamat Petang";
    return "Selamat Malam";
  }, []);

  if (isLoading) return <LoadingPage />;

  return (
    <div className='min-vh-100 position-relative overflow-hidden'>
      <style>{pageStyles}</style>
      {/* Background Image */}
      <div
        className='position-fixed top-0 start-0 w-100 h-100'
        style={{
          backgroundImage: `url(${image})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "brightness(0.7)",
          zIndex: -2,
        }}
        aria-hidden='true'
      />
      {/* Gradient Overlay for better text readability */}
      <div
        className='position-fixed top-0 start-0 w-100 h-100'
        style={{
          background:
            "linear-gradient(135deg, rgba(8,24,48,0.70) 0%, rgba(0,0,0,0.30) 100%)",
          zIndex: -1,
        }}
        aria-hidden='true'
      />

      <Container
        fluid
        className='min-vh-100 d-flex align-items-center justify-content-center p-0'
      >
        <Row className='w-100 g-0'>
          {/* Left side - Hidden on mobile, visible on medium+ screens */}
          <Col
            lg={7}
            md={6}
            className='d-none d-md-flex align-items-center justify-content-center'
          >
            <div
              className='text-white text-center px-4'
              style={{ maxWidth: 820 }}
            >
              <h1
                className='fw-bold mb-3 text-white'
                style={{
                  // Responsive, fluid headline
                  fontSize: "clamp(2rem, 4.5vw, 3.25rem)",
                  lineHeight: 1.15,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                }}
              >
                <span style={{ opacity: 0.95 }}>{greeting},</span>
                <br />
                <span>Selamat Datang ke </span>
                <span
                  // Gradient-accented SMEPPS wordmark
                  style={{
                    background:
                      "linear-gradient(135deg, #FFD166 0%, #06D6A0 50%, #118AB2 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    textShadow: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  SMEPPS
                </span>
              </h1>

              <p
                className='lead mb-4'
                style={{
                  // Comfortable measure + responsive size
                  fontSize: "clamp(1rem, 1.8vw, 1.35rem)",
                  maxWidth: 760,
                  margin: "0 auto",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                  opacity: 0.95,
                }}
              >
                Sistem Maklumat Ekonomi dan Pembangunan Projek Sabah
              </p>

              <div className='d-flex flex-wrap justify-content-center gap-2 mt-4'>
                {MODULES.map((m) => (
                  <span
                    key={m.label}
                    className='signin-module d-inline-flex align-items-center gap-2 rounded-pill px-3 py-2 small fw-bold'
                  >
                    <Icon path={m.icon} size={0.75} />
                    {m.label}
                  </span>
                ))}
              </div>
            </div>
          </Col>

          {/* Right side - Sign in form */}
          <Col
            lg={5}
            md={6}
            className='d-flex flex-column align-items-center justify-content-center p-3 py-4'
          >
            {/* Mobile welcome text - Only visible on small screens */}
            <div className='d-md-none text-center mb-3 px-2'>
              <h2
                className='text-white fw-bold mb-1'
                style={{
                  textShadow: "2px 2px 4px rgba(0,0,0,0.7)",
                  fontSize: "clamp(1.25rem, 6vw, 2rem)",
                }}
              >
                {greeting}
              </h2>
              <p
                className='text-white small mb-0'
                style={{
                  textShadow: "1px 1px 2px rgba(0,0,0,0.7)",
                  opacity: 0.85,
                }}
              >
                Sistem Maklumat Ekonomi dan Pembangunan Projek Sabah
              </p>
            </div>

            <div className='w-100' style={{ maxWidth: "440px" }}>
              <Card
                className='shadow-lg border-0 mb-0'
                style={{
                  borderRadius: "20px",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  backgroundColor: "rgba(255, 255, 255, 0.97)",
                }}
              >
                <Card.Body className='p-4 p-md-5'>
                  {children}
                  <Outlet />
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SignInLayout;
